import { useState, type FormEvent } from 'react';
import { requests } from '../../api/endpoints';
import type { Product } from '../../api/types';
import { Card, Column, DataTable, ErrorBox, Field, label } from '../../components/ui';
import { useAsync } from '../../hooks';
import type { CardData } from '../RequestCardPage';

export function ProductsTab({ data }: { data: CardData }) {
  const products = useAsync<Product[]>(() => requests.products(data.request.id), [data.request.id]);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tnved, setTnved] = useState('');
  const [model, setModel] = useState('');
  const [article, setArticle] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [unit, setUnit] = useState('шт');
  const [itemId, setItemId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const product = await requests.createProduct({
        request: data.request.id,
        name, description, tnved, model, article,
        quantity: quantity || '0',
        unit,
      });
      if (itemId) {
        await requests.linkItemProduct(itemId, product.id);
      }
      setName(''); setDescription(''); setTnved(''); setModel(''); setArticle(''); setQuantity('1');
      products.reload();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };

  const cols: Column<Product>[] = [
    { key: 'name', title: 'Наименование', render: (r) => <strong>{r.name}</strong> },
    { key: 'description', title: 'Описание', render: (r) => r.description || '—' },
    { key: 'tnved', title: 'ТН ВЭД', render: (r) => r.tnved || '—' },
    { key: 'tnved_status', title: 'Статус ТН ВЭД', render: (r) => r.tnved_status ? label(r.tnved_status) : '—' },
    { key: 'model', title: 'Модель', render: (r) => r.model || '—' },
    { key: 'article', title: 'Артикул', render: (r) => r.article || '—' },
    { key: 'quantity', title: 'Кол-во', render: (r) => `${r.quantity ?? '—'} ${r.unit ?? ''}`.trim() },
  ];

  return (
    <Card title="Продукция заявки" actions={
      <button className="btn" onClick={products.reload}>Обновить</button>
    }>
      <ErrorBox error={products.error} onRetry={products.reload} />
      <DataTable columns={cols} rows={products.data ?? []} empty="Продукция не добавлена" />

      <form className="inline-form" onSubmit={submit}>
        <ErrorBox error={err} />
        <div className="form-row">
          <Field label="Наименование"><input value={name} onChange={(e) => setName(e.target.value)} required /></Field>
          <Field label="ТН ВЭД" hint="можно оставить пустым, если код неизвестен">
            <input value={tnved} onChange={(e) => setTnved(e.target.value)} />
          </Field>
        </div>
        <Field label="Техническое описание">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
        </Field>
        <div className="form-row">
          <Field label="Привязать к позиции">
            <select value={itemId} onChange={(e) => setItemId(e.target.value)}>
              <option value="">—</option>
              {data.items.map((it) => <option key={it.id} value={it.id}>{it.title}</option>)}
            </select>
          </Field>
          <Field label="Модель"><input value={model} onChange={(e) => setModel(e.target.value)} /></Field>
          <Field label="Артикул"><input value={article} onChange={(e) => setArticle(e.target.value)} /></Field>
          <Field label="Количество"><input type="number" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} /></Field>
          <Field label="Ед."><input value={unit} onChange={(e) => setUnit(e.target.value)} /></Field>
        </div>
        <div className="form-actions">
          <button className="btn primary" disabled={busy || !name}>{busy ? 'Добавление…' : 'Добавить продукцию'}</button>
        </div>
      </form>
    </Card>
  );
}
